import { PropTypes } from 'prop-types';
import moment from 'moment-timezone';
import { get, isEmpty } from 'lodash';
import {
  getCurrentDayString,
  getCurrentTime,
  readRuleAsMoment,
  readRuleAsBoolean,
  readRuleAsDateString,
  readRuleValue,
  readRule,
  hasRule,
  AVAILABILITY_FILTERS
} from './utils';
import * as constants from '../constants';

const {
  AVAILABILITY_KEY_MAP_FORMAT,
  GROUP_AVAILABILITY_DAILY_KEY_NAME,
  WATERCRAFT_AVAILABILITY_KEY_NAME,
  MEMBER_AVAILABILITY_DAILY_KEY_NAME,
  MEMBER_AVAILABILITY_KEY_NAME,
  QUOTA_TYPE_ADVANCED,
  QUOTA_TYPE_WALKUP,
  OVERRIDE_MAX_VALUE
} = constants;

export const isWalkupQuotaType = (quotaType) => quotaType === QUOTA_TYPE_WALKUP

export const isDayWithinSeason = (dayString, props) => {
  const seasonStart = readRuleAsDateString("SeasonStart", props.aggSeasonRules);
  const seasonEnd = readRuleAsDateString("SeasonEnd", props.aggSeasonRules);

  if (!seasonStart || !seasonEnd) {
    return false;
  }

  return dayString >= seasonStart && dayString <= seasonEnd;
};

export const isDayWithinLotterySeason = (dayString, props) => {
  const lotterySeasonStart = readRuleAsDateString("HighUseSeasonStart", props.aggSeasonRules, props.divisionId);
  const lotterySeasonEnd = readRuleAsDateString("HighUseSeasonEnd", props.aggSeasonRules, props.divisionId);

  if (!lotterySeasonStart || !lotterySeasonEnd) {
    return false;
  }
  return dayString >= lotterySeasonStart && dayString <= lotterySeasonEnd;
};

export const isDayManagedByLottery = (day, props) => {
  const { aggSeasonRules, divisionId } = props;
  const dayString = day.format(AVAILABILITY_KEY_MAP_FORMAT);

  // Backend stores time in timezone of permit converted to utc, reading here as integers for easy comparisons
  const dayBlockedByLotteryStart = readRuleValue('DisplayDate', aggSeasonRules, divisionId);
  const dayBlockedByLotteryEnd = readRuleValue('LotteryReleaseDate', props.permit.rules, divisionId);
  const currentDateString = getCurrentTime({}).unix();

  if (!dayBlockedByLotteryStart || !dayBlockedByLotteryEnd) {
    return false;
  }

  return isDayWithinLotterySeason(dayString, props) &&
    currentDateString >= dayBlockedByLotteryStart && currentDateString < dayBlockedByLotteryEnd;
};

export const isDayWalkupOnly = (day, props) => {
  // walkup quotas are always stored in ConstantQuotaUsageByStartDate as we don't manage them
  // actually, ^^this^^ appears not to be true. Olympic has walkup data in ConstantQuotaUsageDaily,
  // so we're going to look for both options
  const dayFormat = day.startOf('day').format();

  if (get(props, 'quotas.ConstantQuotaUsageDaily')) {
    return get(props, `quotas.ConstantQuotaUsageDaily[${dayFormat}].show_walkup`);
  }

  return get(props, `quotas.ConstantQuotaUsageByStartDate[${dayFormat}].show_walkup`);
};

export const isDayBeingBlockedByLottery = (day, props) => (
  props.blockLotteryDates && isDayManagedByLottery(day, props)
);

export const isDayBlockedWithReason = (day, props) => {
  const todayString = getCurrentDayString(props);
  const dayString = day.format(AVAILABILITY_KEY_MAP_FORMAT)
  if (props.isBypassQuotaCheck) {
    return constants.DAY_UNBLOCKED;
  }
  if (dayString < todayString) {
    // Day is in the past. If it's a retroactive walkup, allow it. Otherwise, block it.
    if (isWalkupQuotaType(props.quotaType)) {
      return constants.DAY_UNBLOCKED;
    }
    return constants.DAY_BLOCKED_DATE_IN_THE_PAST;
  }
  if (!isDayWithinSeason(dayString, props)) {
    return constants.DAY_BLOCKED_OUTSIDE_SEASON;
  }
  if (props.blockNonLotteryDates && !isDayWithinLotterySeason(day, props)) {
    return constants.DAY_BLOCKED_OUTSIDE_LOTTERY_SEASON;
  }
  if (isDayBeingBlockedByLottery(day, props)) {
    return constants.DAY_BLOCKED_BY_LOTTERY;
  }
  if (!props.dateMap) {
    return constants.DAY_BLOCKED_MISSING_QUOTA;
  }
  if (!props.dateMap[day.format(AVAILABILITY_KEY_MAP_FORMAT)]) {
    return constants.DAY_BLOCKED_NO_QUOTA;
  }
  return constants.DAY_UNBLOCKED;
};

export const isDayBlocked = (day, props) => isDayBlockedWithReason(day, props) !== constants.DAY_UNBLOCKED;

export const isExitDayBlocked = (day, props) => {
  if (!props.dateMap) {
    return true;
  }
  if (!props.dateMap[day.format(AVAILABILITY_KEY_MAP_FORMAT)]) {
    return true;
  }

  return false;
}

// TODO using findNextAvailableDay from scratch utils - deprecating this
/*export const findNextAvailableDay = (bools, availabilityProps) => {
  let nextDate;

  Object.keys(bools).forEach((date) => {
    if (!nextDate) {
      if (!isDayBlocked(moment(date, AVAILABILITY_KEY_MAP_FORMAT).utc(), availabilityProps)) {
        nextDate = moment(date, AVAILABILITY_KEY_MAP_FORMAT);
      }
    }
  });

  return nextDate;
};*/

export const isExtendedStayPresent = (permit) => {
  if (!permit || !permit.divisions) {
    return false;
  }

  const result = Object.keys(permit.divisions)
    .filter((divisionId) => hasRule("StayLimitOverLengthInDays", permit.rules, divisionId));

  return result.length !== 0;
};


// returns all known quota types
export const readQuotaFilters = (availabilities, permit, divisions = [], isCommercial, isBypassRulesValidations) => {
  if (!availabilities || !permit || !permit.divisions) {
    return [];
  }
  const quotaTypes = new Set();
  // grab all of the quota types that affect the current divisions (or all divisions if divisions is not an argument)
  Object.keys(availabilities).forEach((availabilityId) => {
    const availability = availabilities[availabilityId];
    if (
      availability.result &&
      permit.id === availability.permitId &&
      (!divisions.length || divisions.findIndex((division) => division === availabilityId) !== -1)
    ) {
      Object.keys(availability.result.quota_type_maps).forEach((quotaType) => {
        quotaTypes.add(quotaType);
      });
    }
  });

  const matchingQuotaFilters = AVAILABILITY_FILTERS(permit, permit.rules, divisions, isCommercial)
    .filter((quotaType) => quotaTypes.has(quotaType.type))
    .sort((a) => {
      if (a.label === 'Groups') {
        return -1;
      }
      return 1;
    });

  if (isBypassRulesValidations) {
    const overrideFilters = matchingQuotaFilters.map((filter) => (filter.maxValue ? { ...filter, maxValue: OVERRIDE_MAX_VALUE } : { ...filter }));
    return overrideFilters;
  }

  return matchingQuotaFilters;
};

export const seedQuotaFilters = (availabilities, permit, activeFilterType, existingFilters) => {
  const quotaFilters = readQuotaFilters(availabilities, permit);
  const resultingFilterValues = {
    mainFilterType: activeFilterType,
    filterValues: existingFilters
  };
  // set initial filter type
  const noFilter = !activeFilterType;
  const activeFilterIsValid = !!quotaFilters.filter((quotaFilter) => quotaFilter.type === activeFilterType)[0];
  if (quotaFilters.length > 0 && (noFilter || !activeFilterIsValid)) {
    resultingFilterValues.mainFilterType = quotaFilters[0].type;
  }

  // set filter values
  const newFilterValues = {};
  quotaFilters
    .forEach((quotaFilter) => {
      if (existingFilters[quotaFilter.type]) {
        newFilterValues[quotaFilter.type] = existingFilters[quotaFilter.type];
      } else {
        newFilterValues[quotaFilter.type] = quotaFilter.initialValue;
      }
    });
  newFilterValues.guides = get(existingFilters, 'guides');
  resultingFilterValues.filterValues = newFilterValues;
  return resultingFilterValues;
};

export const seedExistingQuotaFilters = (availabilities, permit, activeFilterType, existingFilters, existingIssuance, guidesCountTowardsQuota) => {
  if (!existingIssuance) {
    return { mainFilterType: undefined, filterValues: {} };
  }
  const quotaFilters = readQuotaFilters(availabilities, permit);
  const resultingFilterValues = {
    mainFilterType: activeFilterType,
    filterValues: existingFilters
  };

  // set initial filter type
  const noFilter = !activeFilterType;
  const activeFilterIsValid = !!quotaFilters.filter((quotaFilter) => quotaFilter.type === activeFilterType)[0];
  if (quotaFilters.length > 0 && (noFilter || !activeFilterIsValid)) {
    resultingFilterValues.mainFilterType = quotaFilters[0].type;
  }

  // set filter values
  const newFilterValues = {};
  quotaFilters
    .forEach((quotaFilter) => {
      if (existingFilters[quotaFilter.type]) {
        newFilterValues[quotaFilter.type] = existingFilters[quotaFilter.type];
        newFilterValues.guides = existingFilters.guides
      } else if (quotaFilter.type === MEMBER_AVAILABILITY_DAILY_KEY_NAME || quotaFilter.type === MEMBER_AVAILABILITY_KEY_NAME) {
        newFilterValues[quotaFilter.type] = existingIssuance.group_size;
        if (guidesCountTowardsQuota) {
          newFilterValues.guides = get(existingIssuance, 'guides.length', 0);
        }
      } else if (quotaFilter.type === WATERCRAFT_AVAILABILITY_KEY_NAME) {
        const newEquipmentCount = existingIssuance.equipments.reduce((sum, option) => {
          return sum + option.qty;
        }, 0);
        newFilterValues[quotaFilter.type] = newEquipmentCount;  // TODO add case for (pere marquette -equipments)
      } else {
        newFilterValues[quotaFilter.type] = quotaFilter.initialValue;
      }
    });

  if (existingFilters.guides) {
    newFilterValues.guides = existingFilters.guides;
  }
  resultingFilterValues.filterValues = newFilterValues;
  return resultingFilterValues;
};

export const filterAvailability = (availability, filters) => {
  const resultingAvailability = Object.assign({}, availability.bools);
  Object.keys(filters).forEach((filterType) => {
    const filterValue = filters[filterType];
    const quotaTypeMaps = get(availability, 'quota_type_maps', {});
    const quotaTypeMap = quotaTypeMaps[filterType];

    if (!quotaTypeMap) {
      return;
    }

    Object.keys(quotaTypeMap).forEach((rawDate) => {
      const availabilityDateKey = moment(rawDate).utc().format(AVAILABILITY_KEY_MAP_FORMAT);
      const isInitiallyAvailable = resultingAvailability[availabilityDateKey];
      const passesQuotaFilter = quotaTypeMap[rawDate].remaining >= filterValue;
      resultingAvailability[availabilityDateKey] = isInitiallyAvailable && passesQuotaFilter;
    });
  });

  return resultingAvailability;
};

export const getFilterErrors = (filterValues, permit) => {
  const errors = Object.keys(filterValues)
    .map((quotaFilterType) => {
      if (!filterValues[quotaFilterType]) {
        const filter = AVAILABILITY_FILTERS(permit)
          .find((availabilityFilter) => quotaFilterType === availabilityFilter.type);
        return get(filter, 'minValue', 1) > get(filterValues, '[quotaFilterType]', 0) && get(filter, "missingInputError");
      }
      return undefined;
    })
    .filter((errorMessage) => !!errorMessage);
  return errors;
};

export const ignoreQuotaAfterFirstDay = (availability) => {
  if (!availability || !availability.quota_type_maps) {
    return true;
  }

  const doesAvailabilityContainPerDayQuota = (availability.quota_type_maps[GROUP_AVAILABILITY_DAILY_KEY_NAME] ||
    availability.quota_type_maps[WATERCRAFT_AVAILABILITY_KEY_NAME] || (availability.quota_type_maps[MEMBER_AVAILABILITY_DAILY_KEY_NAME]));

  return !doesAvailabilityContainPerDayQuota;
};

export function shouldIgnoreQuotaAfterFirstDay(availabilities) {
  if (!availabilities) {
    return false;
  }
  let doesAnyDivisionHavePerDayQuota = false;
  Object.keys(availabilities).forEach((availabilityId) => {
    doesAnyDivisionHavePerDayQuota = !ignoreQuotaAfterFirstDay(availabilities[availabilityId].result) || doesAnyDivisionHavePerDayQuota;
  });
  return !doesAnyDivisionHavePerDayQuota;
}

export const isWatercraftQuota = (availability) => {
  return !isEmpty(get(availability.quota_type_maps, WATERCRAFT_AVAILABILITY_KEY_NAME, {}));
};

export const isSingleDayStay = (permit) => {
  const rule = readRule('StayLimit', permit.rules);
  return rule.value === (constants.DAYS_TO_SECS / 60);
};

export function parsePermitSeasonInfo(permit) {
  return {
    seasonStart: readRuleAsMoment("SeasonStart", permit.rules, permit.time_zone),
    seasonEnd: readRuleAsMoment("SeasonEnd", permit.rules, permit.time_zone),
    lotterySeasonStart: readRuleAsMoment("HighUseSeasonStart", permit.rules, permit.time_zone),
    lotterySeasonEnd: readRuleAsMoment("HighUseSeasonEnd", permit.rules, permit.time_zone),
    lotteryDisplayDate: readRuleAsMoment('LotteryDisplayDate', permit.rules, permit.time_zone, '', true),
    lotteryReleaseDate: readRuleAsMoment('LotteryReleaseDate', permit.rules, permit.time_zone, '', true)
  };
}

export const AVAILABILITY_PROPTYPES = {
  permit: PropTypes.shape({
    rules: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string
    }))
  }),
  aggSeasonRules: PropTypes.array,
  currentDate: PropTypes.object,
  blockLotteryDates: PropTypes.bool,
  quotaType: PropTypes.oneOf([QUOTA_TYPE_ADVANCED, QUOTA_TYPE_WALKUP]),
  dateMap: PropTypes.object
};

// Checks if account is commercial and has access to the permit
export function hasAccessToPermit(account, permitId) {
  if (account.is_commercial_account) {
    const availablePermit = get(account, ['special_use_permits', permitId]);
    return availablePermit && !availablePermit.is_suspended && moment(availablePermit.expiration).isAfter(moment());
  }
  return true;
}

// Checks if account is commercial and has access to lottery at the permit
export function hasAccessToLottery(account, permit) {
  if (account.is_commercial_account) {
    return readRuleAsBoolean('CommercialLotteryAllowed', permit.rules);
  }
  return true;
}

export { QUOTA_TYPE_ADVANCED, QUOTA_TYPE_WALKUP }
